import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Chip, 
  IconButton, 
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

interface SessionStatusProps {
  showInHeader?: boolean; // Ob in Header oder als separater Bereich angezeigt
  compact?: boolean; // Kompakte Anzeige
}

const SessionStatus: React.FC<SessionStatusProps> = ({ 
  showInHeader = false, 
  compact = false 
}) => {
  const { userRole, sessionTimeRemaining, extendSession } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const [extending, setExtending] = useState(false);

  // Formatiere Zeit in lesbares Format
  const formatTime = (milliseconds: number): string => {
    const totalMinutes = Math.floor(milliseconds / (60 * 1000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Bestimme Farbe basierend auf verbleibender Zeit
  const getStatusColor = (timeRemaining: number) => {
    const minutes = timeRemaining / (60 * 1000);
    if (minutes <= 5) return 'error';
    if (minutes <= 15) return 'warning';
    return 'success';
  };

  // Bestimme Icon basierend auf Status
  const getStatusIcon = (timeRemaining: number) => {
    const minutes = timeRemaining / (60 * 1000);
    if (minutes <= 5) return <WarningIcon fontSize="small" />;
    return <TimeIcon fontSize="small" />;
  };

  // Session manuell verlängern
  const handleExtendSession = async () => {
    setExtending(true);
    try {
      await extendSession();
      setShowDialog(false);
    } catch (error) {
      console.error('Session extension failed:', error);
    } finally {
      setExtending(false);
    }
  };

  // Nicht anzeigen wenn nicht eingeloggt oder Zeit nicht verfügbar
  if (!userRole || sessionTimeRemaining === null) {
    return null;
  }

  const timeRemaining = Math.max(0, sessionTimeRemaining);
  const statusColor = getStatusColor(timeRemaining);
  const statusIcon = getStatusIcon(timeRemaining);
  const formattedTime = formatTime(timeRemaining);
  const progress = Math.max(0, Math.min(100, (timeRemaining / (60 * 60 * 1000)) * 100));

  // Kompakte Anzeige für Header
  if (compact || showInHeader) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={`Session läuft in ${formattedTime} ab. Klicken zum Verlängern.`}>
          <Chip
            icon={statusIcon}
            label={formattedTime}
            color={statusColor}
            variant="outlined"
            size="small"
            onClick={() => setShowDialog(true)}
            sx={{ cursor: 'pointer' }}
          />
        </Tooltip>
        
        {/* Session Extension Dialog */}
        <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
          <DialogTitle>Session-Status</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                Deine Session läuft in <strong>{formattedTime}</strong> ab.
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                color={statusColor}
                sx={{ mt: 1, mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                {progress.toFixed(0)}% der Session-Zeit verbleibt
              </Typography>
            </Box>
            <Typography variant="body2">
              Klicke auf "Verlängern" um deine Session um eine weitere Stunde zu verlängern.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setShowDialog(false)}
              disabled={extending}
            >
              Schließen
            </Button>
            <Button
              onClick={handleExtendSession}
              variant="contained"
              startIcon={<RefreshIcon />}
              disabled={extending}
            >
              {extending ? 'Verlängere...' : 'Session verlängern'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Erweiterte Anzeige für Dashboard/Sidebar
  return (
    <Box 
      sx={{ 
        p: 2, 
        border: 1, 
        borderColor: 'divider', 
        borderRadius: 1,
        bgcolor: 'background.paper'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {statusIcon}
          Session-Status
        </Typography>
        <Tooltip title="Session verlängern">
          <IconButton 
            size="small" 
            onClick={handleExtendSession}
            disabled={extending}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      
      <LinearProgress
        variant="determinate"
        value={progress}
        color={statusColor}
        sx={{ mb: 1 }}
      />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Verbleibt: {formattedTime}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {progress.toFixed(0)}%
        </Typography>
      </Box>
      
      {timeRemaining <= 15 * 60 * 1000 && (
        <Typography 
          variant="caption" 
          color={statusColor === 'error' ? 'error' : 'warning.main'}
          sx={{ mt: 1, display: 'block' }}
        >
          {statusColor === 'error' 
            ? '⚠️ Session läuft bald ab!' 
            : '⏰ Session wird bald ablaufen'
          }
        </Typography>
      )}
    </Box>
  );
};

export default SessionStatus;